import React from 'react';
import { Shield, Award, Users, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About Hi-Viz Pro Kenya Limited</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Your trusted partner in workplace safety solutions across Kenya
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <Card className="border-2 border-orange-200">
            <CardContent className="p-8">
              <Target className="h-12 w-12 text-orange-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-700 leading-relaxed">
                To enhance workplace safety by providing compliant, high-quality, and affordable PPE 
                solutions through reliable supplier partnerships and professional service delivery.
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-200">
            <CardContent className="p-8">
              <Award className="h-12 w-12 text-blue-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-gray-700 leading-relaxed">
                To become a trusted leading provider of high-visibility safety wear and PPE solutions 
                in Kenya and the East African region.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Core Values</h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { title: 'Safety First', desc: 'Prioritizing workplace safety in everything we do' },
              { title: 'Integrity', desc: 'Honest and transparent business practices' },
              { title: 'Reliability', desc: 'Consistent quality and on-time delivery' },
              { title: 'Professionalism', desc: 'Expert service and support' },
              { title: 'Customer Focus', desc: 'Understanding and meeting client needs' }
            ].map((value, idx) => (
              <Card key={idx} className="text-center">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* What We Do */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What We Do</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Services</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span className="text-gray-700">Bulk and project-based PPE supply</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span className="text-gray-700">Corporate branding and logo printing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span className="text-gray-700">PPE sourcing and procurement support</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span className="text-gray-700">Customized safety solutions</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Compliance Standards</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span className="text-gray-700">DOSHS - Directorate of Occupational Safety and Health Services</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span className="text-gray-700">KEBS - Kenya Bureau of Standards</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span className="text-gray-700">EN ISO 20471 - High-Visibility Warning Clothing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span className="text-gray-700">ANSI/ISEA Standards</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Industries We Serve */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Industries We Serve</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Construction & Civil Engineering',
              'Manufacturing & Industrial Plants',
              'Oil, Gas & Energy Sectors',
              'Transportation & Logistics',
              'NGOs & Humanitarian Organizations',
              'Government Departments & Institutions',
              'Electrical & Engineering Firms',
              'Warehouses & Distribution Centers',
              'Event & Security Companies'
            ].map((industry, idx) => (
              <Card key={idx}>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    <span className="text-gray-700">{industry}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
